import {
  createDarkTheme,
  createLightTheme,
  type BrandVariants,
  type Theme,
} from "@fluentui/react-components";
import { createContext, useContext } from "react";

export type AdaptThemeMode = "dark" | "light";
export type AdaptThemeStorage = Pick<Storage, "getItem" | "setItem">;
export type AdaptThemeMatchMedia = (
  query: string,
) => Pick<MediaQueryList, "matches">;

export interface AdaptThemeContextValue {
  themeMode: AdaptThemeMode;
  setThemeMode: (mode: AdaptThemeMode) => void;
  toggleThemeMode: () => void;
}

export interface ResolveInitialAdaptThemeModeOptions {
  defaultMode?: AdaptThemeMode;
  matchMedia?: AdaptThemeMatchMedia;
  storage?: AdaptThemeStorage;
  storageKey?: string;
}

export const ADAPT_THEME_STORAGE_KEY = "adapt-theme-preference";

/**
 * RSI Blue brand palette — matches the legacy ADAPT application.
 * Primary brand: #002F6C (RSI Blue). Follows the FluentUI BrandVariants
 * contract: 10 is darkest anchor, 160 is lightest tint.
 */
export const adaptBrand: BrandVariants = {
  10: "#010206",
  20: "#001023",
  30: "#001D40",
  40: "#002A5D",
  50: "#00377A",
  60: "#194A8D",
  70: "#335DA0",
  80: "#4D70B3",
  90: "#6683C6",
  100: "#8096D9",
  110: "#99A9E0",
  120: "#B3BCE7",
  130: "#CCCFEE",
  140: "#E0E2F5",
  150: "#F0F1FA",
  160: "#F7F8FC",
};

export const adaptLightTheme: Theme = createLightTheme(adaptBrand);

export const adaptDarkTheme: Theme = createDarkTheme(adaptBrand);

export const AdaptThemeContext = createContext<
  AdaptThemeContextValue | undefined
>(undefined);

const browserStorage = (): AdaptThemeStorage | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

const browserMatchMedia = (): AdaptThemeMatchMedia | undefined => {
  try {
    return globalThis.matchMedia?.bind(globalThis);
  } catch {
    return undefined;
  }
};

export const isAdaptThemeMode = (value: unknown): value is AdaptThemeMode =>
  value === "dark" || value === "light";

export const readStoredAdaptThemeMode = (
  storageKey = ADAPT_THEME_STORAGE_KEY,
  storage: AdaptThemeStorage | undefined = browserStorage(),
): AdaptThemeMode | undefined => {
  try {
    const value = storage?.getItem(storageKey);
    return isAdaptThemeMode(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

export const persistAdaptThemeMode = (
  mode: AdaptThemeMode,
  storageKey = ADAPT_THEME_STORAGE_KEY,
  storage: AdaptThemeStorage | undefined = browserStorage(),
): boolean => {
  try {
    storage?.setItem(storageKey, mode);
    return storage !== undefined;
  } catch {
    return false;
  }
};

export const systemAdaptThemeMode = (
  matchMedia: AdaptThemeMatchMedia | undefined = browserMatchMedia(),
): AdaptThemeMode | undefined => {
  try {
    return matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : undefined;
  } catch {
    return undefined;
  }
};

export const resolveInitialAdaptThemeMode = ({
  defaultMode = "light",
  matchMedia = browserMatchMedia(),
  storage = browserStorage(),
  storageKey = ADAPT_THEME_STORAGE_KEY,
}: ResolveInitialAdaptThemeModeOptions = {}): AdaptThemeMode =>
  readStoredAdaptThemeMode(storageKey, storage) ??
  systemAdaptThemeMode(matchMedia) ??
  defaultMode;

export const resolveAdaptTheme = (mode: AdaptThemeMode = "light"): Theme =>
  mode === "dark" ? adaptDarkTheme : adaptLightTheme;

export const useOptionalAdaptTheme = (): AdaptThemeContextValue | undefined =>
  useContext(AdaptThemeContext);

export const useAdaptTheme = (): AdaptThemeContextValue => {
  const context = useOptionalAdaptTheme();
  if (!context) {
    throw new Error("useAdaptTheme must be used inside AdaptShellProvider.");
  }
  return context;
};
